// src/utils/contactSubmission.js
//
// Contact-form submission. Encodes any attachments and POSTs the request to
// the Cloud Function exposed at /api/submitContact (see functions/index.js
// and the Hosting rewrite in firebase.json). The function emails an
// acknowledgement to the visitor and the full request to the director.

export const ALLOWED_EXTENSIONS = [
  // Text
  'txt',
  // PDF
  'pdf',
  // Word
  'doc', 'docx',
  // Excel
  'xls', 'xlsx',
  // PowerPoint
  'ppt', 'pptx',
  // Images
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'tif', 'heic', 'heif',
];

export const ACCEPT_ATTR =
  '.txt,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*';

export const MAX_FILE_SIZE_MB = 20;

// Combined size of all attachments — keeps the request within the
// Cloud Function's payload limit.
export const MAX_TOTAL_SIZE_MB = 20;

export const isAllowedFile = (file) => {
  const name = file.name.toLowerCase();
  const ext = name.split('.').pop();
  if (!ALLOWED_EXTENSIONS.includes(ext)) return false;
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) return false;
  return true;
};

const ENDPOINT = '/api/submitContact';

/** Read a File into the { name, contentType, size, contentBase64 } shape. */
const fileToPayload = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result is a data URL: "data:<type>;base64,<data>"
      const base64 = String(reader.result).split(',')[1] || '';
      resolve({
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        contentBase64: base64,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Submit a contact-form payload + attachments.
 *
 * @param {Object} data - { name, phone, email, company, message }
 * @param {File[]} files
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const submitContactForm = async (data, files) => {
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  if (totalBytes > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
    return {
      ok: false,
      error: `La taille totale des pièces jointes dépasse ${MAX_TOTAL_SIZE_MB} Mo. Merci de réduire ou de regrouper vos fichiers.`,
    };
  }

  try {
    const encodedFiles = await Promise.all(files.map(fileToPayload));

    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name,
        company: data.company,
        email: data.email,
        phone: data.phone,
        message: data.message,
        files: encodedFiles,
      }),
    });

    const result = await res.json().catch(() => ({}));
    if (!res.ok || !result.ok) {
      return {
        ok: false,
        error: result.error || 'Une erreur est survenue. Veuillez réessayer.',
      };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: 'Une erreur est survenue. Veuillez réessayer.',
    };
  }
};
